import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Layers, Search, X } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Stars } from "../components/Stars";
import { API, formatAUD } from "../lib/shop";

const ProductCard = ({ product }) => (
  <Link
    to={`/products/${product.slug}`}
    data-testid={`product-card-${product.letter}`}
    className="group flex flex-col overflow-hidden rounded-sm border border-border bg-card transition-colors hover:border-secondary"
  >
    <div className="relative aspect-square overflow-hidden bg-muted">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <Badge className="absolute left-3 top-3 rounded-sm bg-primary text-primary-foreground hover:bg-primary">
        {product.category}
      </Badge>
    </div>
    <div className="flex flex-1 flex-col p-5">
      <h3 className="text-base font-semibold leading-snug text-foreground">{product.name}</h3>
      {product.rating?.count > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`product-card-rating-${product.letter}`}>
          <Stars value={product.rating.average} size="h-3.5 w-3.5" />
          <span className="font-semibold text-foreground">{product.rating.average}</span>
          ({product.rating.count})
        </div>
      )}
      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{product.blurb}</p>
      {product.matched_variants?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`matched-variants-${product.letter}`}>
          {product.matched_variants.map((v) => (
            <span key={v.product_code} className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] text-secondary">
              {v.product_code}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Layers className="h-3.5 w-3.5" /> {product.variant_count ?? product.matched_variants?.length ?? 0} variants
          </div>
          {product.min_price != null && (
            <div className="mt-1 text-sm font-semibold text-primary" data-testid={`product-card-price-${product.letter}`}>
              From {formatAUD(product.min_price)}
            </div>
          )}
        </div>
        <span className="flex items-center gap-1 text-sm font-medium text-secondary transition-transform group-hover:translate-x-1">
          View <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  </Link>
);

export default function Catalogue() {
  const [products, setProducts] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(console.error);
  }, []);

  // Debounced search by product code / size / description
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      axios
        .get(`${API}/search`, { params: { q } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const showing = results !== null ? results : products;
  const isSearchView = results !== null;

  return (
    <div>
      <section className="border-b border-border bg-primary">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-20">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent">Sydney, Australia</p>
          <h1 className="mt-3 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Your Core Source for Electronic Components
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            DIN rail, ferrules, cable lugs, terminal ends and bimetallic connectors — trade-quality stock for switchboard builders and electricians.
          </p>
          <div className="relative mt-8 max-w-xl">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by product code, size or colour — e.g. CCDR35, 2.5mm, Blue"
              data-testid="catalogue-search-input"
              className="w-full rounded-sm border border-white/20 bg-white py-3 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="catalogue-search-clear"
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8" data-testid="catalogue-grid">
        <h2 className="flex items-baseline gap-3 text-lg font-semibold text-foreground">
          {isSearchView ? `Search results for "${query.trim()}"` : "Product Range"}
          <span className="text-xs font-normal text-muted-foreground/70" data-testid="prices-aud-note">( All prices in AUD per pack )</span>
        </h2>
        {isSearchView && !searching && showing.length === 0 && (
          <p className="mt-6 text-sm text-muted-foreground" data-testid="search-no-results">
            No products or variant codes match "{query.trim()}". Try a shorter code fragment, e.g. "CCF" or "M8".
          </p>
        )}
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {showing === null
            ? Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-sm" />)
            : showing.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}

